import { Link, useRouterState } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import { gebruiktMockApi } from '@/lib/api'
import { initialen } from '@/lib/format'
import { useMe, useProduct } from '@/lib/me-api'
import { isActief, zichtbareNavigatie } from '@/lib/nav'
import { cn } from '@/lib/utils'
import { GemaaktDoor } from './GemaaktDoor'

export function Merk() {
  return (
    <div className="flex h-14 shrink-0 items-center border-b border-hairline px-5">
      <Link to="/" className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primair-600 text-[11px] font-semibold text-canvas">
          NT2
        </span>
        <span className="flex flex-col leading-tight">
          <span className="text-sm font-semibold tracking-[-0.01em] text-ink">Inzetplanning</span>
          <span className="text-[11px] text-ink-faint">Windesheim</span>
        </span>
      </Link>
    </div>
  )
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const pad = useRouterState({ select: (state) => state.location.pathname })
  const product = useProduct()

  return (
    <nav className="flex flex-1 flex-col gap-0.5 px-3 pt-4">
      {zichtbareNavigatie(product).map((item) => {
        const actief = isActief(item, pad)
        const Icon = item.icon
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={cn(
              'group relative flex items-center gap-3 rounded-lg py-2 pl-3 pr-3 text-sm font-medium transition-colors duration-150',
              actief
                ? 'bg-primair-600/10 text-primair-700'
                : 'text-ink-soft hover:bg-fill-2 hover:text-ink',
            )}
          >
            {actief ? (
              <span
                aria-hidden="true"
                className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-primair-600"
              />
            ) : null}
            <Icon
              className={cn(
                'h-[18px] w-[18px]',
                actief ? 'text-primair-600' : 'text-ink-faint group-hover:text-ink-soft',
              )}
              strokeWidth={actief ? 2.1 : 1.85}
            />
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

export function SidebarFooter({ onNavigate }: { onNavigate?: () => void }) {
  const { data } = useMe()
  const gebruiker = data?.user

  function uitloggen() {
    onNavigate?.()
    window.location.assign(gebruiktMockApi ? '/login' : '/auth/uitloggen')
  }

  return (
    <div className="border-t border-hairline px-3 py-3">
      <div className="flex items-center gap-3 rounded-xl bg-fill-1 px-2.5 py-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primair-600/12 text-xs font-semibold text-primair-700">
          {gebruiker ? initialen(gebruiker.naam) : '··'}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{gebruiker?.naam ?? 'Laden'}</p>
          <p className="truncate text-xs text-ink-faint">{gebruiker?.email ?? ''}</p>
        </div>
        <button
          type="button"
          onClick={uitloggen}
          className="btn-ghost h-8 w-8 shrink-0 p-0"
          aria-label="Uitloggen"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.85} />
        </button>
      </div>

      <GemaaktDoor className="mt-3 px-2.5" />
    </div>
  )
}

export function Sidebar() {
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-hairline bg-surface lg:flex">
      <Merk />
      <SidebarNav />
      <SidebarFooter />
    </aside>
  )
}
